/**
 * Density test for the Metals & Non-metals Lab (CT-INTERACT): the learner types a sample mass and
 * the volume of water it displaces, and the model works out ρ = m ÷ V and compares it with the
 * densities of the reviewed solid lab samples. Only solid samples are listed because the
 * displacement method needs a solid. Densities come from the periodic-table dataset
 * (periodicTableData.ts, CRC values at room temperature); an entry that does not read as a plain
 * g/cm³ value is left out rather than guessed.
 */
import { ELEMENT_LAB_SAMPLES, elementTestRecord } from './metalsNonmetals';
import { firstModelError, type ExploreChallenge, type ModelField, type WorkedStep } from './modelInputContract';
import { PERIODIC_ELEMENTS } from './periodicTableData';

export type DensityInputKey = 'massG' | 'volumeMl';

export const DENSITY_FIELDS: readonly ModelField<DensityInputKey>[] = [
  { key: 'massG', label: 'Sample mass', unit: 'g', min: 0.1, max: 2000, step: 0.1 },
  { key: 'volumeMl', label: 'Water displaced', unit: 'mL', min: 0.1, max: 500, step: 0.1 },
];

/** A measured density within this percentage of a reviewed value counts as consistent with it. */
export const DENSITY_MATCH_TOLERANCE_PERCENT = 5;

export interface ReviewedDensity {
  symbol: string;
  name: string;
  densityText: string;
  density: number;
}

const DENSITY_PATTERN = /^(\d+(?:\.\d+)?) g\/cm³(?: \([a-z ]+\))?$/;

/** Solid lab samples whose dataset density reads as a plain g/cm³ value. */
export const REVIEWED_SOLID_DENSITIES: readonly ReviewedDensity[] = ELEMENT_LAB_SAMPLES.flatMap((sample) => {
  const record = elementTestRecord(sample.symbol);
  const entry = PERIODIC_ELEMENTS.find((element) => element.symbol === sample.symbol);
  const match = entry?.density.match(DENSITY_PATTERN);
  if (!record || record.state !== 'solid' || !match) return [];
  return [{ symbol: sample.symbol, name: sample.name, densityText: match[1], density: Number(match[1]) }];
});

export interface DensityResult {
  density: number;
  steps: WorkedStep[];
  /** The single reviewed sample within tolerance, or null when none or more than one fits. */
  match: ReviewedDensity | null;
  /** Every reviewed sample within tolerance. */
  candidates: ReviewedDensity[];
  limitNote: string | null;
}

function fmt(value: number, digits: number): string {
  return value.toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function percentOff(measured: number, reviewed: number): number {
  return (Math.abs(measured - reviewed) / reviewed) * 100;
}

/** Worked density and comparison. Throws the field message for invalid input. */
export function calculateDensityTest(input: Record<DensityInputKey, number>): DensityResult {
  const error = firstModelError(DENSITY_FIELDS, input);
  if (error) throw new Error(error);
  const density = input.massG / input.volumeMl;
  const densityText = fmt(density, 2);

  const ranked = [...REVIEWED_SOLID_DENSITIES].sort(
    (left, right) => percentOff(density, left.density) - percentOff(density, right.density),
  );
  const closest = ranked[0];
  const off = percentOff(density, closest.density);
  const candidates = ranked.filter((entry) => percentOff(density, entry.density) <= DENSITY_MATCH_TOLERANCE_PERCENT);
  const match = candidates.length === 1 ? candidates[0] : null;

  const comparison = `|${densityText} − ${closest.densityText}| ÷ ${closest.densityText} × 100 = ${fmt(off, 1)} %`;
  const verdict =
    candidates.length === 0
      ? `> ${DENSITY_MATCH_TOLERANCE_PERCENT} % → no reviewed match`
      : candidates.length === 1
        ? `≤ ${DENSITY_MATCH_TOLERANCE_PERCENT} % → consistent with ${closest.name.toLowerCase()}`
        : `≤ ${DENSITY_MATCH_TOLERANCE_PERCENT} %, but ${candidates.map((entry) => entry.name.toLowerCase()).join(' and ')} all fit`;

  const steps: WorkedStep[] = [
    {
      id: 'density',
      title: 'Density',
      formula: 'ρ = m ÷ V (1 mL of displaced water = 1 cm³)',
      working: `ρ = ${fmt(input.massG, 1)} g ÷ ${fmt(input.volumeMl, 1)} cm³ = ${densityText} g/cm³`,
      status: 'done',
    },
    {
      id: 'compare',
      title: `Closest reviewed sample: ${closest.name} (${closest.densityText} g/cm³)`,
      formula: 'difference = |ρ − ρ(sample)| ÷ ρ(sample) × 100',
      working: `${comparison} ${verdict}`,
      status: 'done',
    },
  ];

  const limitNote =
    candidates.length === 0
      ? `No reviewed solid sample is within ${DENSITY_MATCH_TOLERANCE_PERCENT} % of ${densityText} g/cm³. The tool does not guess an element outside its list; check the mass and the volume reading.`
      : candidates.length > 1
        ? `Density alone cannot decide between ${candidates.map((entry) => entry.name.toLowerCase()).join(' and ')}. Use the property tests as well.`
        : null;

  return { density, steps, match, candidates, limitNote };
}

/** Default measurements: a copper sample, 44.8 g displacing 5.0 mL (ρ = 8.96 g/cm³). */
export const DEFAULT_DENSITY_INPUT: Record<DensityInputKey, number> = { massG: 44.8, volumeMl: 5 };

/** Input to the teaching tasks: the learner's measurements with the result they produced. */
export interface DensityChallengeInput {
  input: Record<DensityInputKey, number>;
  result: DensityResult;
}

export const DENSITY_QUESTION = 'Can you identify an element from its density, and when is density alone not enough?';

const COPPER_SYMBOL = 'Cu';
/** Ten times the default copper sample mass. */
const BIG_COPPER_MASS_G = 448;

/** Teaching tasks (CT-INTERACT C7). Each check reads only the measurements and the model result. */
export const DENSITY_CHALLENGES: ExploreChallenge<DensityChallengeInput>[] = [
  {
    id: 'identify-another',
    prompt: 'Identify a different element: change the mass and volume until exactly one sample other than copper matches.',
    hint: 'Pick a sample from the bars and choose a mass and volume with m ÷ V close to its density.',
    isMet: ({ result }) => result.match !== null && result.match.symbol !== COPPER_SYMBOL,
    why: 'Density is a property of the material: 26.5 g ÷ 10.0 mL = 2.65 g/cm³ is within 5 % of aluminium (2.70 g/cm³), so the sample is consistent with aluminium.',
  },
  {
    id: 'bigger-copper',
    prompt: 'Keep the result as copper, but use a sample at least 10 times heavier (448 g or more).',
    hint: 'A bigger piece also pushes away more water.',
    isMet: ({ input, result }) => result.match?.symbol === COPPER_SYMBOL && input.massG >= BIG_COPPER_MASS_G,
    why: 'A heavier object is not denser. 448 g of copper displaces 50.0 mL, and 448 ÷ 50.0 = 8.96 g/cm³: mass and volume grow together, so the density stays the same.',
  },
  {
    id: 'two-fit',
    prompt: 'Find a density that two different elements both fit, so density alone cannot decide.',
    hint: 'Look for two bars of almost the same length.',
    isMet: ({ result }) => result.candidates.length >= 2,
    why: 'Silicon (2.33 g/cm³) and graphite (2.26 g/cm³) are only 3 % apart, so 23.0 g ÷ 10.0 mL = 2.30 g/cm³ fits both. Use the property tests, such as conductivity and lustre, to decide.',
  },
];
